import {
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useNavigationType, useLocation  } from 'react-router-dom';
import { font } from '../../Common/font';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import {
  deleteFirstWeightEntry,
  fetchFirstWeightEntryList,
  resetFirstWeightEntryData,
  resetFirstWeightEntryList,
} from '../../slices/first-weight-entry';
import moment from 'moment';
import { debounce } from 'lodash';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import logo from '../../Images/alux-logo.png';

export default function FirstWeightEntryList() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const { user: currentUser } = useSelector(state => state.auth);
  const { data = [], totalRecord, status } = useSelector(state => state.firstWeightEntry);
   const isMountedRef = useRef(true);
  const [search, setSearch] = useState(localStorage.getItem('die_text_search_tool') || '');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(100);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const location = useLocation();
  const navigationType = useNavigationType();
  console.log(navigationType);
  console.log(typeof navigationType);
  useEffect(() => {
    console.log("🔄 useEffect fired");
    console.log("📍 Pathname:", location.pathname);
    console.log("🗝️ Key:", location.key);
    console.log("🚦 NavigationType:", navigationType);
  }, [location.key]);
  const [checkedList, setCheckedList] = useState([
    "serial_no",
    "weight_for",
    "gross_weight",
    "tare_weight",
    "net_weight",
    "weight_automatic",
    "date_time_first",
    "date_time_second",
    "mound",
    "vehicle_no",
    "vehicle_type_name",
    "party_name_display",
    "cash_party_name",
    "party_mobile_no",
    "material_name",
    "purchaser_name",
    "seller_name",
    "total_copy",
    "id",
    "action",
  ]);



  // Set mounted ref on component mount and cleanup on unmount
  useEffect(() => {
    console.log("First useEffect");
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    console.log("Second useEffect");
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [dispatch, ipAddress]);

  const debouncedSearch = useMemo(
    () => debounce((query) => {
      if (!isMountedRef.current) return;
      
      setIsSearching(true);
      
      if (query.length >= 3 || query.length === 0) {
        // Clear existing data before new search
        dispatch(resetFirstWeightEntryData());
        
        dispatch(fetchFirstWeightEntryList({ 
          page: 1, 
          pagesize,
          ...(query ? { search: query } : {}),
          ...(orderBy ? { ordering: orderBy } : {}),
        }))
        .finally(() => {
          if (isMountedRef.current) {
            setIsSearching(false);
          }
        });
        
        setPage(1); // Reset to first page on search
      } else {
        setIsSearching(false);
      }
    }, 300), // Reduced debounce time for better UX
    [dispatch, pagesize, orderBy]
  );

  // Initial data fetch and when dependencies change
  useEffect(() => {
    console.log("Third useEffect");
    if (!isMountedRef.current) return;

    // Clear data first for clean loading state
    dispatch(resetFirstWeightEntryData());
    
    // Fetch new data
    dispatch(fetchFirstWeightEntryList({
      page,
      pagesize,
      ...(search ? { search } : {}),
      ...(orderBy ? { ordering: orderBy } : {}),
    }));
  }, [dispatch, page, pagesize, orderBy]);

  

   // Cleanup debounced function
  useEffect(() => {
    console.log("Fourth useEffect");
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Optimized handlers with useCallback
  // Optimized handlers with useCallback
  const resetFilter = useCallback(() => {
    setSearch('');
    setOrderBy('');
    setPage(1);
    setIsSearching(false);
    localStorage.removeItem('die_text_search_tool');
    localStorage.removeItem('die_group_search_tool');
    
    // Clear data first then fetch
    dispatch(resetFirstWeightEntryData());
    dispatch(fetchFirstWeightEntryList({ page: 1, pagesize }));
  }, [dispatch, pagesize]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((current, size) => {
    setPage(1);
    setPageSize(size);
  }, []);

  // Optimized sorting function using lookup object
  const sortingMap = useMemo(() => ({
  serial_no: { asc: 'serial_no', desc: '-serial_no' },
  weight_for: { asc: 'weight_for', desc: '-weight_for' },
  gross_weight: { asc: 'gross_weight', desc: '-gross_weight' },
  tare_weight: { asc: 'tare_weight', desc: '-tare_weight' },
  net_weight: { asc: 'net_weight', desc: '-net_weight' },
  weight_automatic: { asc: 'weight_automatic', desc: '-weight_automatic' },
  date_time_first: { asc: 'date_time_first', desc: '-date_time_first' },
  date_time_second: { asc: 'date_time_second', desc: '-date_time_second' },
  mound: { asc: 'mound', desc: '-mound' },
  vehicle_no: { asc: 'vehicle_no', desc: '-vehicle_no' },
  vehicle_type_name: { asc: 'vehicle_type_name', desc: '-vehicle_type_name' },
  party_name_display: { asc: 'party_name_display', desc: '-party_name_display' },
  cash_party_name: { asc: 'cash_party_name', desc: '-cash_party_name' },
  party_mobile_no: { asc: 'party_mobile_no', desc: '-party_mobile_no' },
  material_name: { asc: 'material_name', desc: '-material_name' },
  purchaser_name: { asc: 'purchaser_name', desc: '-purchaser_name' },
  seller_name: { asc: 'seller_name', desc: '-seller_name' },
  total_copy: { asc: 'total_copy', desc: '-total_copy' },
  }), []);

 const GetSortingEnumValue = useCallback((sortField, sortOrder) => {
    const field = sortingMap[sortField];
    if (field) {
      return sortOrder === 'ascend' ? field.asc : field.desc;
    }
    return 'id';
  }, [sortingMap]);


 const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const newOrderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(newOrderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  }, [GetSortingEnumValue]);

 const handleSearch = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    
    // Clear data immediately for better UX
    if (value.length >= 3 || value.length === 0) {
      dispatch(resetFirstWeightEntryData());
    }
    
    // Trigger debounced search
    debouncedSearch(value);
  }, [debouncedSearch, dispatch]);

  const handleEdit = useCallback((data) => {
    navigate(`/edit-first-weight-entry/${data.id}`);
  }, [navigate]);

  const handleDelete = useCallback((id) => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteFirstWeightEntry(id))
      .unwrap()
      .then(() => {
        message.success('First Weight Entry Archive Successfully!');
      })
      .catch(err => {
        console.error('Delete Error:', err);
        message.error(`Error: ${err || 'Failed to delete the profile.'}`);
      });
  }, [dispatch]);

  const debouncedAPICall = useMemo(() =>
    debounce((query) => {
      if (query.length >= 3) {
        dispatch(fetchFirstWeightEntryList({ search: query }));
        localStorage.setItem('die_text_search_tool', query);
      }
    }, 1000), [dispatch]
  );

  useEffect(() => {
    console.log("Fifth useEffect");
    return () => {
      debouncedAPICall.cancel(); // Clean up on unmount
    };
  }, [debouncedAPICall]);

  const deviationStatusMap = {
    active: 'Active',
    inactive: 'Inactive',
    die_broken: 'Die Broken',
    die_absolute: 'Die Absolute',
    die_shift: 'Die Shift',
    other: 'Other',
  };

 const handleColumnVisibilityChange = useCallback((checkedValues) => {
    setCheckedList(checkedValues);
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Sr. No.',
      dataIndex: 'id',
      key: 'id',
      width: '80px',
      render: (text, record, index) => (page - 1) * pagesize + index + 1,
      sorter: false,
      align: 'center'
    },
    {
        title: "Serial No",
        dataIndex: "serial_no",
        key: "serial_no",
        render: (text) => text || "N/A",
      },
      {
        title: "Weight For",
        dataIndex: "weight_for",
        key: "weight_for",
        render: (text) => text || "N/A",
      },
      {
        title: "Gross Weight",
        dataIndex: "gross_weight",
        key: "gross_weight",
        render: (text) => text ?? 0,
      },
      {
        title: "Tare Weight",
        dataIndex: "tare_weight",
        key: "tare_weight",
        render: (text) => text ?? 0,
      },
      {
        title: "Net Weight",
        dataIndex: "net_weight",
        key: "net_weight",
        render: (text) => text ?? 0,
      },
      {
        title: "Weight Automatic",
        dataIndex: "weight_automatic",
        key: "weight_automatic",
        render: (text) => (text ),
      },
      {
        title: "Date & Time First",
        dataIndex: "date_time_first",
        key: "date_time_first",
        render: (record) => (
          <span>
            {record ? dayjs(record).format("DD-MM-YYYY hh:mm:ss A") : "N/A"}
          </span>
        ),
      },
      {
        title: "Date & Time Second",
        dataIndex: "date_time_second",
        key: "date_time_second",
        render: (record) => (
          <span>
            {record ? dayjs(record).format("DD-MM-YYYY hh:mm:ss A") : "N/A"}
          </span>
        ),
      },
      {
        title: "Mound",
        dataIndex: "mound",
        key: "mound",
        render: (text) => text ?? 0,
      },
      {
        title: "Vehicle No.",
        dataIndex: "vehicle_no",
        key: "vehicle_no",
        render: (text) => text || "N/A",
      },
      {
        title: "Vehicle Type",
        dataIndex: "vehicle_type_name",
        key: "vehicle_type_name",
        render: (text) => text || "N/A",
      },
      {
        title: "Party Name",
        dataIndex: "party_name_display",
        key: "party_name_display",
        render: (text) => text || "N/A",
      },
      {
        title: "Cash Party Name",
        dataIndex: "cash_party_name",
        key: "cash_party_name",
        render: (text) => text || "N/A",
      },
      {
        title: "Party Mobile No",
        dataIndex: "party_mobile_no",
        key: "party_mobile_no",
        render: (text) => text || "N/A",
      },
      {
        title: "Material",
        dataIndex: "material_name",
        key: "material_name",
        render: (text) => text || "N/A",
      },
      {
        title: "Purchaser",
        dataIndex: "purchaser_name",
        key: "purchaser_name",
        render: (text) => text || "N/A",
      },
      {
        title: "Seller",
        dataIndex: "seller_name",
        key: "seller_name",
        render: (text) => text || "N/A",
      },
      {
        title: "Total Copy",
        dataIndex: "total_copy",
        key: "total_copy",
        render: (text) => text ?? 1,
      },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record ? `${record.first_name} ${record.last_name}` : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record?.trim() ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      width: '100px',
      sorter: true,
      render: record => (
        <span>
          {record?.first_name?.trim() && record?.last_name?.trim()
            ? `${record.first_name} ${record.last_name}`
            : 'N/A'}
        </span>
      ),
    },
   
    {
      title: 'Action',
      dataIndex: 'id',
      key: 'action',
      width: '100px',
      fixed: 'right',
      render: (text, record) => (
        <Space>
          {currentUser.permissions.permissions.die.includes('change_dietool') && (
            <Tooltip placement="top" title={'Edit'}>
              <span onClick={() => handleEdit(record)}>
                <EditIcon style={{ cursor: 'pointer' }} />
              </span>
            </Tooltip>
          )}
          {currentUser.permissions.permissions.die.includes('delete_dietool') && (
            <Tooltip placement="top" title={'Archive'}>
              <Popconfirm
                title="Are you sure you want to archive this record?"
                onConfirm={() => handleDelete(record.id)}
                onCancel={() => message.info('Action cancelled')}
                okText="Yes"
                cancelText="No"
              >
                <Archive style={{ cursor: 'pointer' }} />
              </Popconfirm>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ], [page, pagesize, currentUser, handleEdit, handleDelete]);

  const filteredColumns = columns.filter(column =>
    checkedList.includes(column.key),
  );

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group value={checkedList} onChange={handleColumnVisibilityChange}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Checkbox value="id" disabled>
                Sr. No.
              </Checkbox>
              </Col>
              <Col xs={24} md={12}>
            <Checkbox value="serial_no">Serial No.</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="weight_for">Weight For</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="gross_weight">Gross Wt</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="tare_weight">Tare Wt</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="net_weight">Net Wt</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="weight_automatic">Weight Automatic</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="date_time_first">Date & Time First</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="date_time_second">Date & Time Second</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="mound">Mound</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="vehicle_no">Vehicle No.</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="vehicle_type_name">Vehicle Type</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="party_name_display">Party Name</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="cash_party_name">Cash Party Name</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="party_mobile_no">Party Mobile No.</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="material_name">Material</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="purchaser_name">Purchaser</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="seller_name">Seller</Checkbox>
          </Col>
          <Col xs={24} md={12}>
            <Checkbox value="total_copy">Total Copy</Checkbox>
          </Col>
            <Col xs={24} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const exportFirstWeightEntryToPDF = pdfdata => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');

    const allColumns = [
        { header: 'Serial No', dataKey: 'serial_no', field: 'serial_no' },
        { header: 'Weight For', dataKey: 'weight_for', field: 'weight_for' },
        { header: 'Gross Weight', dataKey: 'gross_weight', field: 'gross_weight' },
        { header: 'Tare Weight', dataKey: 'tare_weight', field: 'tare_weight' },
        { header: 'Net Weight', dataKey: 'net_weight', field: 'net_weight' },
        { header: 'Weight Automatic', dataKey: 'weight_automatic', field: 'weight_automatic' },
        { header: 'Date & Time First', dataKey: 'date_time_first', field: 'date_time_first' },
        { header: 'Date & Time Second', dataKey: 'date_time_second', field: 'date_time_second' },
        { header: 'Mound', dataKey: 'mound', field: 'mound' },
        { header: 'Vehicle No.', dataKey: 'vehicle_no', field: 'vehicle_no' },
        { header: 'Vehicle Type', dataKey: 'vehicle_type_name', field: 'vehicle_type_name' },
        { header: 'Party Name', dataKey: 'party_name_display', field: 'party_name_display' },
        { header: 'Cash Party Name', dataKey: 'cash_party_name', field: 'cash_party_name' },
        { header: 'Party Mobile No', dataKey: 'party_mobile_no', field: 'party_mobile_no' },
        { header: 'Material', dataKey: 'material_name', field: 'material_name' },
        { header: 'Purchaser', dataKey: 'purchaser_name', field: 'purchaser_name' },
        { header: 'Seller', dataKey: 'seller_name', field: 'seller_name' },
        { header: 'Total Copy', dataKey: 'total_copy', field: 'total_copy' },
      { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
      { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
    ];

    const columns = Array.isArray(checkedList)
      ? allColumns.filter(col => checkedList.includes(col.field))
      : [];

    const rows = Array.isArray(pdfdata)
      ? pdfdata.map(item => {
        const row = {};
        columns.forEach(col => {
          switch (col.field) {
            case 'tool_number':
              row.toolNumber = item?.die?.die_number
                ? `${item.die.die_number}-${item.tool_number}`
                : 'N/A';
              break;
            case 'current_kg':
              row.current_kg = item?.current_kg
                ? `${item.current_kg}`
                : 'N/A';
              break;
            case 'order_date':
              row.order_date = item?.order_date
                ? dayjs(item.order_date).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'received_date':
              row.received_date = item?.received_date
                ? dayjs(item.received_date).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'die_size':
              row.toolSize = item?.die_size?.die_width
                ? `${item.die_size.die_width} X ${item.die_size.die_height}`
                : 'N/A';
              break;
            case 'die_cavity':
              row.dieCavity = item.die_cavity || 'N/A';
              break;
            case 'is_active':
              row.status = item.is_active ? true : false;
              break;
            case 'total_running_qty':
              row.runningQty = item.total_running_qty || '0';
              break;
            case 'rac_no':
              row.rac_no = item.rac_no || '0';
              break;
            case 'row_no':
              row.row_no = item.row_no || '0';
              break;
            case 'column_no':
              row.column_no = item.column_no || '0';
              break;
            case 'developer':
              row.developer = item.developer?.vendor_registered_name || 'N/A';
              break;
            case 'ownership':
              row.ownership = item.ownership || 'N/A';
              break;
            case 'drawing_no':
              row.drawing_no = item.drawing_no || 'N/A';
              break;
            case 'developer_ref_no':
              row.developer_ref_no = item.developer_ref_no || 'N/A';
              break;
            case 'total_running_ton':
              row.total_running_ton = item.total_running_ton || '0';
              break;
            case 'run_under_deviation':
              row.deviationStatus = item.run_under_deviation
                ? deviationStatusMap[item.run_under_deviation] || 'Under Development'
                : 'N/A';
              break;
            case 'purchase_price':
              row.purchase_price =
                `${item.purchase_price?.toLocaleString({ maximumFractionDigits: 0 })}` ||
                '0';
              break;
            case 'remarks':
              row.remarks = item.remarks || 'N/A';
              break;
            case 'created_at':
              row.created_at = item.created_at
                ? moment(item.created_at).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'created_by':
              row.created_by = item.created_by
                ? `${item.created_by.first_name} ${item.created_by.last_name}`
                : 'N/A';
              break;
            case 'updated_at':
              row.updated_at = item.updated_at
                ? moment(item.updated_at).format('DD-MM-YYYY')
                : 'N/A';
              break;
            case 'updated_by':
              row.updated_by = item.updated_by
                ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
                : 'N/A';
              break;
            default:
              break;
          }
        });
        return row;
      })
      : [];

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = 150;
    const imgHeight = 40;
    pdf.addImage(logo, 'PNG', pageWidth - imgWidth - 40, 20, imgWidth, imgHeight);
    pdf.setFontSize(16);
    pdf.text('Profile Tool', pageWidth / 2, 40, { align: 'center' });
    pdf.autoTable({
      columns,
      body: rows,
      startY: 80,
      theme: 'grid',
      margin: { top: 80, right: 30, left: 30, bottom: 40 },
      styles: {
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.8,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      columnStyles: {
        price: { halign: 'right' },
        runningQty: { halign: 'right' },
      },
      didDrawPage: (data) => {
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pageWidth = pdf.internal.pageSize.getWidth();

        // Footer - Date & Time on left
        pdf.setFontSize(8);
        pdf.setTextColor(100);
        const now = moment();
        const dateStr = `${now.format("DD-MM-YYYY hh:mm:ss A")}`;
        pdf.text(dateStr, 40, pageHeight - 20);

        // Footer - Page number on right
        const pageStr = `Page ${data.pageNumber}`;
        pdf.text(pageStr, pageWidth - 40, pageHeight - 20, { align: "right" });
      },
      willDrawCell: pdfdata => {
        if (pdfdata.cell.text.length > 0) {
          const text = pdfdata.cell.text[0];
          if (text && text.length > 30) {
            pdfdata.cell.text[0] = text.substring(0, 27) + '...';
          }
        }
      },
    });
    pdf.save(`profile_tool${new Date().toISOString().split('T')[0]}.pdf`);
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'FirstWeightEntry',
        ip_address: ipAddress,
        discription: 'First Weight Entry pdf download successfully!',
      }),
    );
  };

  const csvheaders = [
    { label: 'Serial No.', key: 'serial_no' },
  { label: 'Weight For', key: 'weight_for' },
  { label: 'Gross Weight', key: 'gross_weight' },
  { label: 'Tare Weight', key: 'tare_weight' },
  { label: 'Net Weight', key: 'net_weight' },
  { label: 'Weight Automatic', key: 'weight_automatic' },
  { label: 'Date & Time First', key: 'date_time_first' },
  { label: 'Date & Time Second', key: 'date_time_second' },
  { label: 'Mound', key: 'mound' },
  { label: 'Vehicle No.', key: 'vehicle_no' },
  { label: 'Vehicle Type', key: 'vehicle_type_name' },
  { label: 'Party Name', key: 'party_name_display' },
  { label: 'Cash Party Name', key: 'cash_party_name' },
  { label: 'Party Mobile No.', key: 'party_mobile_no' },
  { label: 'Material', key: 'material_name' },
  { label: 'Purchaser', key: 'purchaser_name' },
  { label: 'Seller', key: 'seller_name' },
  { label: 'Total Copy', key: 'total_copy' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];
  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvListData = data?.map((item, index) => ({
    serial_no: item?.serial_no || 'N/A',
    weight_for: item?.weight_for || 'N/A',
    gross_weight: item?.gross_weight ?? 0,
    tare_weight: item?.tare_weight ?? 0,
    net_weight: item?.net_weight ?? 0,
    weight_automatic: item?.weight_automatic || 'N/A',
    date_time_first: item?.date_time_first
      ? moment(item?.date_time_first).format('DD-MM-YYYY hh:mm:ss A')
      : 'N/A',
    date_time_second: item?.date_time_second
      ? moment(item?.date_time_second).format('DD-MM-YYYY hh:mm:ss A')
      : 'N/A',
    mound: item?.mound ?? 0,
    vehicle_no: item?.vehicle_no || 'N/A',
    vehicle_type_name: item?.vehicle_type_name || 'N/A',
    party_name_display: item?.party_name_display || 'N/A',
    cash_party_name: item?.cash_party_name || 'N/A',
    party_mobile_no: item?.party_mobile_no || 'N/A',
    material_name: item?.material_name || 'N/A',
    purchaser_name: item?.purchaser_name || 'N/A',
    seller_name: item?.seller_name || 'N/A',
    total_copy: item?.total_copy ?? 1,
    created_at: item?.created_at
      ? moment(item?.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item?.created_by
      ? `${item?.created_by.first_name} ${item?.created_by.last_name}`
      : 'N/A',
    updated_at: item?.updated_at
      ? moment(item?.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item?.updated_by
      ? `${item?.updated_by.first_name} ${item?.updated_by.last_name}`
      : 'N/A',
  }));

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'FirstWeightEntry',
        ip_address: ipAddress,
        discription: 'First Weight Entry excel download successfully!',
      }),
    );
  }
  return (
    <div className='mainwrap'>
      <div className="sticyaction">
        <Row className="pagenamerow mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>First Weight Entry List</h2>
            <div className="bredcrumbwrp">
                <Link
                  className="back"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <Back /> BACK
                </Link>
                <Breadcrumb
                  items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: 'First Weight Entry List' },
                  ]}
                />
              </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className='searchbox'
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                style={{ width: 200 }}
              />
              <Tooltip placement="top" title="Reset Filter">
                <Button
                  type="primary"
                  disabled={!search && !orderBy}
                  className="iconlink"
                  onClick={resetFilter}
                >
                  <FilterIcon />
                </Button>
              </Tooltip>
              {currentUser.permissions.permissions.die.includes(
                'download_profile_tool_excel_copy',
              ) && (
                  <Tooltip placement="top" title={'Export Excel'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                      onClick={activityLog}
                    >
                      <CSVLink
                        filename={'profile_tool.csv'}
                        data={csvListData}
                        className="iconlink"
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die.includes(
                'download_profile_tool_pdf_copy',
              ) && (
                  <Tooltip placement="top" title={'Export PDF'}>
                    <Button
                      type="primary"
                      className="iconlink"
                      onClick={() => exportFirstWeightEntryToPDF(data)}
                    >
                      <FilePdfOutlined />
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die.includes(
                'add_dietool',
              ) && (
                  <Link to="/add-first-weight-entry">
                    <Tooltip placement="top" title={'Add First Weight Entry'}>
                      <Button type="primary" icon={<PlusOutlined />}>
                        Add First Weight Entry
                      </Button>
                    </Tooltip>
                  </Link>
                )}
              {currentUser.permissions.permissions.die.includes(
                'delete_dietool',
              ) && (
                  <Tooltip placement="top" title="Archive">
                    <Link to="/archive-first-weight-entry" className="iconlink">
                      {' '}
                      <Archiveicon style={{ cursor: 'pointer' }} />
                    </Link>
                  </Tooltip>
                )}
                 <div className="gridiconswrap">
              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      <div className='tablewrp'>
        <Table
          rowKey="id"
          dataSource={data}
          columns={filteredColumns}
          pagination={false}
          loading={status === 'loading'}
          onChange={handleTableChange}
          scroll={{ x: '1000px' }}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={["20", "50", "100"]}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />


    </div >
  );
}
